import type {
  BusArrival,
  BusRoute,
  CrowdingAggregate,
  CrowdingReport,
  FerryNetwork,
  FerryStopDepartures,
  FerryTripTimeline,
  GeocodeResult,
  NjBusNetwork,
  NjBusStopDepartures,
  NjBusTripTimeline,
  RailNetwork,
  RailStationDepartures,
  RailTripTimeline,
  ServiceAlert,
  StopTimeUpdate,
  SystemStatus,
  TripModes,
  TripPlanPoint,
  TripPlanResponse,
  VehiclePosition,
} from '@traintopia/contracts'
import type { Stop } from '../store/rider'
import { apiPath } from '@traintopia/contracts'
import { API_BASE_URL as BASE_URL } from './env'

export type {
  BusArrival,
  BusLeg,
  BusRoute,
  CrowdingAggregate,
  CrowdingReport,
  FerryDeparture,
  FerryDepartureGroup,
  FerryLanding,
  FerryNetwork,
  FerryRoute,
  FerryStopDepartures,
  FerryTripTimeline,
  GeocodeResult,
  Itinerary,
  NjBusDeparture,
  NjBusNetwork,
  NjBusRoute,
  NjBusStop,
  NjBusStopDepartures,
  NjBusTripTimeline,
  RailDeparture,
  RailNetwork,
  RailRoute,
  RailStation,
  RailStationDepartures,
  RailTripTimeline,
  ServiceAlert,
  StopTimeUpdate,
  SubwayLeg,
  SystemStatus,
  TripLeg,
  TripModes,
  TripPlace,
  TripPlanPoint,
  TripPlanResponse,
  VehiclePosition,
  WalkLeg,
} from '@traintopia/contracts'

export interface StopWithRoutes {
  /** Representative GTFS station id for the complex — see `memberIds`. */
  id: string
  name: string
  lat: number
  lon: number
  routes: string[]
  /** Every line reachable on one fare at any hour — `routes` plus anything linked to this
   *  station but kept in a separate complex. Same as `routes` at all but a handful of
   *  stations. Optional, so a client on an older backend still parses. */
  transferRoutes?: string[]
  /** Lines reachable only while a part-time passage is open (Bryant Park → Times Sq through
   *  the 42 St Connection, shut midnight–6am). Hours are local, `closesHour` exclusive. */
  limitedTransfers?: Array<{ routes: string[]; opensHour: number; closesHour: number }>
  /** Every GTFS parent station MTA folds into this complex, including `id`. Any member is
   *  accepted by the details/arrivals endpoints, so pre-merge favorites still resolve. */
  memberIds: string[]
  accessible?: boolean
}

export interface StationExit {
  name: string
  lat: number
  lon: number
  street: string
  corner: string
  ada: boolean
}

/** One upcoming train: a time paired with its trip. The tripId is what makes an arrival
 *  drillable into its full timetable via `getTripUpdates`. */
export interface ArrivalTime {
  tripId: string
  time: number
}

export interface StopArrival {
  routeId: string
  direction: 'N' | 'S' | ''
  times: ArrivalTime[]
}

export interface StationDetails {
  id: string
  name: string
  lat: number
  lon: number
  routes: string[]
  exits: StationExit[]
  accessible: boolean
}

/** Thrown when a request is rejected by a server-side rate limit (HTTP 429). */
export class RateLimitError extends Error {
  constructor(public retryAfterSeconds: number) {
    super(`Rate limited; retry after ${retryAfterSeconds}s`)
    this.name = 'RateLimitError'
  }
}

type RequestJsonOptions =
  | { method?: 'GET'; body?: never }
  | { method: 'POST'; body: unknown }

async function requestJson<T>(path: string, options: RequestJsonOptions = {}): Promise<T> {
  const isPost = options.method === 'POST'
  const res = await fetch(`${BASE_URL}${path}`, {
    ...(isPost ? { method: 'POST', body: JSON.stringify(options.body) } : {}),
    headers: isPost
      ? { 'Content-Type': 'application/json', Accept: 'application/json' }
      : { Accept: 'application/json' },
    signal: AbortSignal.timeout(10_000),
  })
  if (res.status === 429) {
    const parsedRetryAfter = Number(res.headers.get('Retry-After'))
    const retryAfter = Number.isFinite(parsedRetryAfter) && parsedRetryAfter > 0
      ? parsedRetryAfter
      : 60
    throw new RateLimitError(retryAfter)
  }
  if (!res.ok) throw new Error(`API error ${res.status} on ${path}`)
  return res.json() as Promise<T>
}

const get = <T>(path: string): Promise<T> => requestJson<T>(path)
const post = <T>(path: string, body: unknown): Promise<T> =>
  requestJson<T>(path, { method: 'POST', body })

export const api = {
  getVehicles: () => get<{ vehicles: VehiclePosition[]; count: number }>(apiPath.vehicles()),
  getVehicle: (id: string) => get<{ vehicle: VehiclePosition }>(apiPath.vehicle(id)),
  getRouteVehicles: (routeId: string) =>
    get<{ vehicles: VehiclePosition[] }>(apiPath.routeVehicles(routeId)),
  getTripUpdates: (tripId: string) =>
    get<{ tripId: string; updates: StopTimeUpdate[] }>(apiPath.tripUpdates(tripId)),
  // Live onward-stop list for a boarded bus (by stored vehicle id, e.g. "NYCT_5500").
  getBusVehicleStops: (vehicleId: string) =>
    get<{ vehicleId: string; updates: StopTimeUpdate[] }>(apiPath.busVehicleStops(vehicleId)),
  getAlerts: () => get<{ alerts: ServiceAlert[]; count: number }>(apiPath.alerts()),
  /** Planned work not yet in effect. Filtered here too: a server predating `?scope=` ignores
   *  it and answers with every alert. */
  getUpcomingAlerts: () =>
    get<{ alerts: ServiceAlert[]; count: number }>(apiPath.alerts('upcoming'))
      .then((r) => r.alerts.filter((a) => a.upcoming)),
  getRouteAlerts: (routeId: string) =>
    get<{ alerts: ServiceAlert[] }>(apiPath.routeAlerts(routeId)),
  getStops: () => get<{ stops: Stop[]; count: number }>(apiPath.stops()),
  getStopsWithRoutes: () => get<{ stops: StopWithRoutes[] }>(apiPath.stopsWithRoutes()),
  // Station id → lines with a train actually serving it, for narrowing a static route list
  // to real service (no W on a Sunday). Every member id of a complex is a key. An empty
  // `stops` map means the live view is unavailable — fall back to the static list, never to
  // an empty one.
  getLiveStationRoutes: () =>
    get<{ updatedAt: number; stops: Record<string, string[]> }>(apiPath.liveStationRoutes()),
  getBusRoutes: () => get<{ routes: BusRoute[]; count: number }>(apiPath.busRoutes()),
  getStopCoords: () => get<Record<string, { lat: number; lon: number }>>(apiPath.stopCoords()),
  getStationDetails: (id: string) => get<StationDetails>(apiPath.stationDetails(id)),
  // `times` = upcoming trains per route+direction. The backend caps at a one-hour horizon,
  // so a large number yields "everything in the next hour" — what "See more" shows.
  getStopArrivals: (id: string, times?: number) =>
    get<{ arrivals: StopArrival[] }>(
      apiPath.stopArrivals(id, times),
    ),
  getBusStopArrivals: (id: string) =>
    get<{ stopId: string; arrivals: BusArrival[] }>(apiPath.busStopArrivals(id)),
  getRouteShapes: () => get<GeoJSON.FeatureCollection>(apiPath.routeShapes()),
  healthCheck: () => get<{ status: string; timestamp: string }>(apiPath.health()),

  // Feed liveness + headways. Normally pushed as SYSTEM_STATUS; this is the socket-down
  // fallback.
  getSystemStatus: () => get<SystemStatus>(apiPath.systemStatus()),

  // ── Trip planner ──
  planTrip: (
    from: TripPlanPoint,
    to: TripPlanPoint,
    departAt?: number,
    modes?: TripModes,
    // Omitted = the backend default (express buses allowed).
    expressBus?: boolean,
  ) =>
    get<TripPlanResponse>(
      apiPath.tripPlan({ from, to, departAt, modes, expressBus }),
    ),
  geocode: (text: string) =>
    get<{ results: GeocodeResult[] }>(apiPath.geocode(text)),

  // ── NYC Ferry ──
  // Routes, landings and route lines in one payload; static between timetable changes.
  getFerryNetwork: () => get<FerryNetwork>(apiPath.ferryNetwork()),
  // `hours` = how far ahead to list departures; ferries run every 20–60 min, so the
  // backend's default is 3 h rather than the subway board's one.
  getFerryDepartures: (stopId: string, hours?: number) =>
    get<FerryStopDepartures>(apiPath.ferryStopDepartures(stopId, hours)),
  // One trip's whole run, same `updates` shape as `getTripUpdates`, so TripTimetable
  // renders it unchanged.
  getFerryTrip: (tripId: string) => get<FerryTripTimeline>(apiPath.ferryTrip(tripId)),

  // ── LIRR + Metro-North ──
  // Branches, stations and track lines for both railroads in one payload.
  getRailNetwork: () => get<RailNetwork>(apiPath.railNetwork()),
  // A station's board, time-ordered across branches; the backend defaults to 3 h.
  getRailDepartures: (stationId: string, hours?: number) =>
    get<RailStationDepartures>(apiPath.railStationDepartures(stationId, hours)),
  // One trip's whole run, same `updates` shape as `getTripUpdates`.
  getRailTrip: (tripId: string) => get<RailTripTimeline>(apiPath.railTrip(tripId)),

  // ── NJ Transit buses ──
  // Routes, stops and route lines in one payload (~2.8 MB raw, gzipped in transit), loaded
  // only when the NJ Bus mode is picked.
  getNjBusNetwork: () => get<NjBusNetwork>(apiPath.njBusNetwork()),
  // A stop's board, time-ordered across routes; the backend defaults to 2 h.
  getNjBusDepartures: (stopId: string, hours?: number) =>
    get<NjBusStopDepartures>(apiPath.njBusStopDepartures(stopId, hours)),
  // One trip's whole run, same `updates` shape as `getTripUpdates`.
  getNjBusTrip: (tripId: string) => get<NjBusTripTimeline>(apiPath.njBusTrip(tripId)),

  // ── Crowdsourced crowding ──
  getCrowding: (vehicleId: string) =>
    get<CrowdingAggregate>(apiPath.crowding(vehicleId)),
  reportCrowding: (report: CrowdingReport) =>
    post<CrowdingAggregate & { accepted: boolean }>(apiPath.reportCrowding(), report),
}
